import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { EmptySearchList } from '../../../../../components/empty-search-list/index';
import RootLoader from '../../../../../components/root-loader/index';
import { ScenarioHooks } from '../../../../../hooks/index';
import {
  CapTableTab,
  Column,
  columnTitle,
  shareClassesViewByColumns,
  StakeholdersViewBy,
  stakeholdersViewByColumns,
} from '../fundraising.types';
import { ShareClassesTab, StakeholdersTab } from '../tabs/index';

export type TableProps = {
  companyId: string;
  isEmptySearchResult: boolean;
  scenarioId: string;
  tab: CapTableTab;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export type TabProps = {
  companyId: string;
  scenarioId: string;
  selectedViewBy: 'all' | StakeholdersViewBy;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export const capTableViewBy: Record<CapTableTab, (props: TabProps) => ReactNode> = {
  [CapTableTab.BY_SHARE_CLASS]: (props) => <ShareClassesTab {...props} />,
  [CapTableTab.BY_STAKEHOLDER]: (props) => <StakeholdersTab {...props} />,
};

export const Table: FC<TableProps> = ({
  viewBy,
  visibleColumns,
  tab,
  companyId,
  scenarioId,
  isEmptySearchResult,
}) => {
  const {
    data: [shareClassOrderData] = [],
    isLoading,
    isSuccess,
  } = ScenarioHooks.useCapTableStakeholderTypes({ scenarioId });

  if (isEmptySearchResult) return <EmptySearchList className="m-auto my-20" />;
  if (isLoading || !isSuccess) return <RootLoader />;

  const selectedViewBy: 'all' | StakeholdersViewBy =
    viewBy?.stakeholders && viewBy?.type
      ? 'all'
      : viewBy?.type
        ? StakeholdersViewBy.TYPE
        : StakeholdersViewBy.STAKEHOLDERS;

  const additionalColumns: PartialKeys<Column, string[]> = {
    [Column.OTHER_SHARE_CLASSES]: shareClassOrderData?.shareClasses
      ?.filter(({ isDefault }) => !isDefault)
      ?.map(({ name }) => (name.length > 30 ? name.substring(0, 30) + '...' : name)),
  };

  const columns =
    tab === CapTableTab.BY_SHARE_CLASS
      ? shareClassesViewByColumns
      : stakeholdersViewByColumns[selectedViewBy];

  return (
    <>
      <table className="w-full shrink-0 overflow-hidden text-nowrap">
        <thead className="h-11 border-y border-[#F2F2F2] bg-gray-50 px-4">
          <tr>
            <th className="pl-4 text-left">
              <span className="text-label-md font-medium text-[#172335]" />
            </th>
            {columns.map(
              (fieldNameKey) =>
                visibleColumns.includes(fieldNameKey) &&
                (additionalColumns?.[fieldNameKey] ? (
                  additionalColumns?.[fieldNameKey].map((fieldName) => (
                    <th className="px-4 pr-6 text-left" key={fieldName}>
                      <span className="text-label-md font-medium text-[#172335]">{fieldName}</span>
                    </th>
                  ))
                ) : (
                  <th className="px-4 pr-6 text-left" key={fieldNameKey}>
                    <span className="text-label-md font-medium text-[#172335]">
                      <AppFormattedMessage id={columnTitle[fieldNameKey]} />
                    </span>
                  </th>
                )),
            )}
          </tr>
        </thead>
        <tbody>
          {capTableViewBy[tab]({
            visibleColumns,
            companyId,
            selectedViewBy,
            viewBy,
            scenarioId,
          })}
        </tbody>
      </table>
    </>
  );
};
